import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { tokens } from './tokens';

/** Glass panel language matching product cards. */
export const surfaceSx = {
  bgcolor: tokens.colors.surface,
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: tokens.colors.border,
  borderRadius: `${tokens.radii.card}px`,
  boxShadow: tokens.shadows.card,
} as const;

export const pressableBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  borderRadius: 2,
  minHeight: 44,
  transitionProperty: 'transform, background-color, box-shadow, border-color, color',
  transitionDuration: '120ms',
  transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
  '&:active': { transform: 'scale(0.96)' },
};

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: tokens.colors.surface,
    backgroundImage: 'none',
  },
  '& .MuiSelect-select': {
    bgcolor: 'transparent',
  },
} as const;

export const dialogPaperSx = {
  bgcolor: tokens.colors.bgElevated,
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: tokens.colors.border,
  borderRadius: `${tokens.radii.card}px`,
  boxShadow: tokens.shadows.dialog,
} as const;

export const menuPaperSx = {
  bgcolor: tokens.colors.bgElevated,
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: tokens.colors.border,
} as const;

export const chipSx = (selected?: boolean) =>
  ({
    height: 40,
    bgcolor: selected ? 'action.selected' : tokens.colors.surface,
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: selected ? tokens.colors.accent : tokens.colors.border,
    transitionProperty: 'border-color, background-color, transform',
    transitionDuration: '120ms',
    '&:hover': {
      borderColor: selected ? tokens.colors.accent : tokens.colors.borderStrong,
    },
    '&:active': { transform: 'scale(0.96)' },
    '& .MuiChip-label': {
      fontWeight: selected ? 600 : 500,
      px: 1.25,
    },
    '& .MuiChip-deleteIcon': {
      color: 'text.secondary',
      '&:hover': { color: 'error.main' },
    },
  }) as const;

type SurfaceProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
};

/** Bordered glass panel for forms, lists, and nested groups. */
export function Surface({ children, sx, component = 'div' }: SurfaceProps) {
  return (
    <Box component={component} sx={{ ...surfaceSx, ...((sx as object) || {}) }}>
      {children}
    </Box>
  );
}
